import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import { useAuth } from "./AuthContext";

const DEFAULT_API_URL = "https://api-dev.virgilipsas.com";
const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL ||
  process.env.REACT_APP_API_URL ||
  DEFAULT_API_URL;

const BudgetSocketContext = createContext(null);

const getConnectionLabel = ({ isOnline, status, error }) => {
  if (!isOnline) {
    return "Disconnected - internet required";
  }

  if (status === "connected") {
    return "Connected";
  }

  if (status === "connecting") {
    return "Reconnecting...";
  }

  if (error) {
    return "Disconnected - internet required";
  }

  return "Disconnected - internet required";
};

export const BudgetSocketProvider = ({ children }) => {
  const { token } = useAuth();
  const socketRef = useRef(null);
  const [status, setStatus] = useState("disconnected");
  const [error, setError] = useState("");
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [workspaceRevision, setWorkspaceRevision] = useState(0);
  const [changeRequestRevision, setChangeRequestRevision] = useState(0);
  const [workspaceUpdate, setWorkspaceUpdate] = useState(null);
  const [changeRequestUpdate, setChangeRequestUpdate] = useState(null);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      socketRef.current?.connect();
    };
    const handleOffline = () => {
      setIsOnline(false);
      setStatus("disconnected");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!token) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      setStatus("disconnected");
      setError("");
      return undefined;
    }

    setStatus("connecting");
    setError("");

    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ["websocket", "polling"],
      reconnection: true,
    });

    socketRef.current = socket;

    const handleConnect = () => {
      setStatus("connected");
      setError("");
      socket.timeout(10000).emit("budget:join", {}, () => {});
    };
    const handleConnectError = (connectError) => {
      setStatus("disconnected");
      setError(connectError?.message || "Unable to connect to budget socket");
    };
    const handleDisconnect = () => {
      setStatus("disconnected");
    };
    const handleReconnectAttempt = () => {
      setStatus("connecting");
    };
    const handleWorkspaceUpdated = (payload = {}) => {
      setWorkspaceRevision((currentValue) => currentValue + 1);
      setWorkspaceUpdate({
        ...payload,
        received_on: Date.now(),
      });
    };
    const handleChangeRequestUpdated = (payload = {}) => {
      setChangeRequestRevision((currentValue) => currentValue + 1);
      setChangeRequestUpdate({
        ...payload,
        received_on: Date.now(),
      });
    };

    socket.on("connect", handleConnect);
    socket.on("connect_error", handleConnectError);
    socket.on("disconnect", handleDisconnect);
    socket.on("budget:workspace:updated", handleWorkspaceUpdated);
    socket.on("budget:change-request:updated", handleChangeRequestUpdated);
    socket.io.on("reconnect_attempt", handleReconnectAttempt);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("connect_error", handleConnectError);
      socket.off("disconnect", handleDisconnect);
      socket.off("budget:workspace:updated", handleWorkspaceUpdated);
      socket.off("budget:change-request:updated", handleChangeRequestUpdated);
      socket.io.off("reconnect_attempt", handleReconnectAttempt);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token]);

  const emitWithAck = useCallback((eventName, payload, timeoutMessage, failureMessage) => {
    return new Promise((resolve, reject) => {
      const socket = socketRef.current;

      if (!isOnline || !socket?.connected) {
        reject(new Error("Socket disconnected. Internet connection is required."));
        return;
      }

      socket.timeout(10000).emit(eventName, payload, (timeoutError, response) => {
        if (timeoutError) {
          reject(new Error(timeoutMessage));
          return;
        }

        if (!response || response.status !== 200) {
          reject(new Error(response?.message || failureMessage));
          return;
        }

        resolve(response);
      });
    });
  }, [isOnline]);

  const saveWorkspace = useCallback((payload) => {
    return emitWithAck(
      "budget:workspace:save",
      payload,
      "Budget save timed out. Please check your connection.",
      "Unable to save budget workspace.",
    );
  }, [emitWithAck]);

  const createChangeRequest = useCallback((payload) => {
    return emitWithAck(
      "budget:change-request:create",
      payload,
      "Change request timed out. Please check your connection.",
      "Unable to create change request.",
    );
  }, [emitWithAck]);

  const withdrawChangeRequest = useCallback((requestId) => {
    return emitWithAck(
      "budget:change-request:withdraw",
      { request_id: requestId },
      "Change request withdrawal timed out. Please check your connection.",
      "Unable to withdraw change request.",
    );
  }, [emitWithAck]);

  const respondChangeRequest = useCallback((requestId, action, remark) => {
    return emitWithAck(
      "budget:change-request:respond",
      { request_id: requestId, action, remark },
      "Change request response timed out. Please check your connection.",
      "Unable to respond to change request.",
    );
  }, [emitWithAck]);

  const value = useMemo(() => {
    const statusLabel = getConnectionLabel({ isOnline, status, error });
    const visibleStatus = isOnline ? status : "disconnected";

    return {
      connected: visibleStatus === "connected",
      connecting: visibleStatus === "connecting",
      disconnected: visibleStatus !== "connected",
      error,
      isOnline,
      saveWorkspace,
      createChangeRequest,
      withdrawChangeRequest,
      respondChangeRequest,
      workspaceRevision,
      changeRequestRevision,
      workspaceUpdate,
      changeRequestUpdate,
      status: visibleStatus,
      statusLabel,
    };
  }, [
    changeRequestRevision,
    changeRequestUpdate,
    createChangeRequest,
    error,
    isOnline,
    respondChangeRequest,
    saveWorkspace,
    status,
    withdrawChangeRequest,
    workspaceUpdate,
    workspaceRevision,
  ]);

  return (
    <BudgetSocketContext.Provider value={value}>
      {children}
    </BudgetSocketContext.Provider>
  );
};

export const useBudgetSocket = () => {
  return useContext(BudgetSocketContext);
};
